import { useAuth0 } from '@auth0/auth0-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';

import { getBookingGenerationJob } from '../apis/bookingApi';
import { Message } from '../types';

const BOOKING_JOB_POLL_INTERVAL = 5000;

/**
 * Polls booking generation job status for messages that have a bookingGenerationJobId
 * but no receiptId yet. Invalidates messages cache when job completes/fails so the
 * message refetches with the newly populated receiptId.
 */
export const useBookingJobPolling = (
  messages: Message[],
  chatId: string | null
) => {
  const { getAccessTokenSilently } = useAuth0();
  const queryClient = useQueryClient();

  const pendingMessage = useMemo(() => {
    return messages.find(
      msg => msg.bookingGenerationJobId && !msg.receiptId
    );
  }, [messages]);

  const pendingBookingJobId = pendingMessage?.bookingGenerationJobId ?? null;

  useQuery({
    queryKey: ['booking-generation', pendingBookingJobId],
    queryFn: async () => {
      if (!pendingBookingJobId) return null;
      const token = await getAccessTokenSilently();
      return getBookingGenerationJob(pendingBookingJobId, token);
    },
    enabled: !!pendingBookingJobId && !!chatId,
    refetchInterval: query => {
      const job = query.state.data;
      if (!job) return BOOKING_JOB_POLL_INTERVAL;
      if (job.status === 'completed' || job.status === 'failed') return false;
      return BOOKING_JOB_POLL_INTERVAL;
    },
    refetchIntervalInBackground: false,
    staleTime: 0,
    gcTime: 60 * 1000,
    select: data => {
      if (!data || !chatId) return data;

      if (data.status === 'completed' || data.status === 'failed') {
        queryClient.invalidateQueries({ queryKey: ['messages', chatId] });
      }

      return data;
    },
  });
};
